import React, { useMemo, useState } from "react";
import { ClockIcon, SunIcon } from "@heroicons/react/24/outline";
import { Typography } from "@material-tailwind/react";
import { formatTimeMxFlexible, hourInMexicoCity } from "../../utils/dateMx";

function groupSlotOptions(items) {
  const morning = [];
  const afternoon = [];
  for (const row of items) {
    const h = hourInMexicoCity(row.startUtc);
    if (h < 12) morning.push(row);
    else afternoon.push(row);
  }
  return { morning, afternoon };
}

function SlotSkeleton() {
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2.5" aria-hidden>
      {Array.from({ length: 8 }).map((_, i) => (
        <div
          key={i}
          className="h-14 rounded-xl bg-gradient-to-r from-brand-brown/10 via-brand-brown/5 to-brand-brown/10 animate-pulse"
          style={{ animationDelay: `${i * 80}ms` }}
        />
      ))}
    </div>
  );
}

/**
 * Rejilla de horarios: disponibles (clic) y ocupados / no disponibles (atenuados).
 * `slotOptions` tiene prioridad sobre `slots` (solo libres).
 */
export default function TimeSlotGrid({
  slotOptions,
  slots,
  loading,
  selectedSlot,
  onSelect,
  disabledIso,
  disableAll = false,
}) {
  const [hour12, setHour12] = useState(false);

  const rows = useMemo(() => {
    if (slotOptions?.length) return slotOptions;
    return (slots || []).map((startUtc) => ({ startUtc, available: true }));
  }, [slotOptions, slots]);

  const { morning, afternoon } = useMemo(() => groupSlotOptions(rows), [rows]);

  if (loading) {
    return (
      <div className="space-y-3">
        <Typography className="text-xs font-medium uppercase tracking-wide text-brand-gray">
          Cargando horarios…
        </Typography>
        <SlotSkeleton />
      </div>
    );
  }

  if (rows.length === 0) {
    return null;
  }

  const anyAvailable = rows.some((r) => r.available);

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <Typography className="text-xs text-brand-gray">
          {anyAvailable
            ? "Gris = ya ocupado. Elige un horario libre."
            : "No quedan horarios libres este día."}
        </Typography>
        <button
          type="button"
          onClick={() => setHour12((v) => !v)}
          className="text-xs font-semibold text-brand-lavender hover:underline tabular-nums"
        >
          {hour12 ? "Usar 24 h" : "Usar 12 h"}
        </button>
      </div>

      <div className="space-y-8">
        {morning.length > 0 && (
          <section aria-labelledby="slots-morning">
            <div className="flex items-center gap-2 mb-3">
              <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-amber-100 text-amber-700">
                <SunIcon className="h-5 w-5" aria-hidden />
              </span>
              <div>
                <Typography
                  id="slots-morning"
                  className="text-sm font-semibold text-brand-dark"
                >
                  Mañana
                </Typography>
                <p className="text-xs text-brand-gray">Antes del mediodía (CDMX)</p>
              </div>
            </div>
            <div
              role="listbox"
              aria-label="Horarios en la mañana"
              className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2.5"
            >
              {morning.map((row, i) => (
                <SlotButton
                  key={row.startUtc}
                  row={row}
                  index={i}
                  hour12={hour12}
                  selected={selectedSlot === row.startUtc}
                  disabled={disableAll || disabledIso === row.startUtc}
                  onSelect={onSelect}
                />
              ))}
            </div>
          </section>
        )}

        {afternoon.length > 0 && (
          <section aria-labelledby="slots-afternoon">
            <div className="flex items-center gap-2 mb-3">
              <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-indigo-100 text-indigo-700">
                <ClockIcon className="h-5 w-5" aria-hidden />
              </span>
              <div>
                <Typography
                  id="slots-afternoon"
                  className="text-sm font-semibold text-brand-dark"
                >
                  Tarde
                </Typography>
                <p className="text-xs text-brand-gray">Desde mediodía (CDMX)</p>
              </div>
            </div>
            <div
              role="listbox"
              aria-label="Horarios en la tarde"
              className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2.5"
            >
              {afternoon.map((row, i) => (
                <SlotButton
                  key={row.startUtc}
                  row={row}
                  index={i + morning.length}
                  hour12={hour12}
                  selected={selectedSlot === row.startUtc}
                  disabled={disableAll || disabledIso === row.startUtc}
                  onSelect={onSelect}
                />
              ))}
            </div>
          </section>
        )}
      </div>
    </div>
  );
}

function SlotButton({ row, index, hour12, selected, disabled, onSelect }) {
  const { startUtc, available } = row;
  const timeLabel = formatTimeMxFlexible(startUtc, hour12);

  if (!available) {
    return (
      <div
        role="presentation"
        aria-label={`${timeLabel}, no disponible`}
        style={{ animationDelay: `${index * 42}ms` }}
        className="animate-slot-in rounded-xl border-2 border-dashed border-brand-brown/25 bg-stone-100/90 px-3 py-3.5 text-center min-h-[3.25rem] flex items-center justify-center"
      >
        <span className="tabular-nums text-sm font-medium text-brand-gray line-through decoration-brand-brown/40">
          {timeLabel}
        </span>
      </div>
    );
  }

  return (
    <button
      type="button"
      role="option"
      aria-selected={selected}
      disabled={disabled}
      onClick={() => onSelect(startUtc)}
      style={{ animationDelay: `${index * 42}ms` }}
      className={[
        "animate-slot-in relative rounded-xl px-3 py-3.5 text-center transition-all duration-200",
        "border-2 font-semibold text-sm min-h-[3.25rem] flex flex-col items-center justify-center",
        "hover:scale-[1.03] active:scale-[0.98] disabled:opacity-60 disabled:hover:scale-100",
        selected
          ? "border-brand-lavender bg-brand-lavender/12 text-brand-navy shadow-md shadow-brand-lavender/15"
          : "border-brand-lavender/40 bg-white text-brand-dark hover:border-brand-lavender hover:bg-brand-lavender/8",
      ].join(" ")}
    >
      <span className="tabular-nums">{timeLabel}</span>
      {selected && (
        <span className="absolute -top-1 -right-1 flex h-5 w-5 items-center justify-center rounded-full bg-brand-lavender text-[10px] text-white font-bold">
          ✓
        </span>
      )}
    </button>
  );
}
